import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './pages/LandingPageFull';
import Signup from './pages/Signup';
import Login from './pages/Login';
import OrgLogin from './pages/OrgLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OAuthCallback from './pages/OAuthCallback';
import SuperAdminLogin from './pages/SuperAdminLogin';
import AcceptInvite from './pages/AcceptInvite';

// Heavy/authenticated pages — code-split so cold boot stays small.
// Suspense fallback (RouteSplash) shows a pulsing S only if the chunk
// takes more than ~250ms to download.
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const ClientDashboard = React.lazy(() => import('./pages/ClientDashboard'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const AccountSettings = React.lazy(() => import('./pages/AccountSettings'));
const Payments = React.lazy(() => import('./pages/Payments'));
const Communications = React.lazy(() => import('./pages/Communications'));
const ScheduledReports = React.lazy(() => import('./pages/ScheduledReports'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const Logs = React.lazy(() => import('./pages/Logs'));
const CRM = React.lazy(() => import('./pages/CRM'));
const Branding = React.lazy(() => import('./pages/Branding'));
const AISettings = React.lazy(() => import('./pages/AISettings'));
const Proposals = React.lazy(() => import('./pages/Proposals'));
const Clients = React.lazy(() => import('./pages/Clients'));
const Performance = React.lazy(() => import('./pages/Performance'));
const Tasks = React.lazy(() => import('./pages/Tasks'));
const FirmPerformance = React.lazy(() => import('./pages/FirmPerformance'));
const ClientPerformance = React.lazy(() => import('./pages/ClientPerformance'));
const ClientPortal = React.lazy(() => import('./pages/ClientPortal'));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const OnboardingWizard = React.lazy(() => import('./pages/OnboardingWizard'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const ConsultantInbox = React.lazy(() => import('./pages/ConsultantInbox'));
const ConsultantPlaybooks = React.lazy(() => import('./pages/ConsultantPlaybooks'));

import FeatureGuard from './components/FeatureGuard';
import SplashScreen from './components/SplashScreen';
import RouteSplash from './components/RouteSplash';
import WelcomeTour from './components/WelcomeTour';
import { OrganizationProvider, useSubdomain } from './contexts/OrganizationContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Always tell the backend which public domain the request originated from, so
// outbound emails (password reset, invites, client onboarding) embed the right
// link even when an ingress rewrites Host/Origin headers along the way.
axios.defaults.headers.common['X-Frontend-Origin'] = window.location.origin;

export const AuthContext = React.createContext(null);

// Component to handle subdomain-based routing
function SubdomainHandler({ children }) {
  const subdomain = useSubdomain();
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const resolveOrg = async () => {
      if (!subdomain) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/resolve-subdomain/${subdomain}`);
        setOrgData(response.data);
        
        // Store the organization in sessionStorage for the current session
        sessionStorage.setItem('current_org', JSON.stringify(response.data));
        
        // Apply custom branding CSS variables
        if (response.data.primary_color) {
          document.documentElement.style.setProperty('--org-primary', response.data.primary_color);
        }
        if (response.data.secondary_color) {
          document.documentElement.style.setProperty('--org-secondary', response.data.secondary_color);
        }
      } catch (err) {
        console.error('Failed to resolve subdomain:', err);
        setError('Organization not found');
      } finally {
        setLoading(false);
      }
    };

    resolveOrg();
  }, [subdomain]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading {subdomain}...</p>
        </div>
      </div>
    );
  }

  if (error && subdomain) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800">Organization Not Found</h1>
          <p className="mt-2 text-slate-600">The subdomain "{subdomain}" does not exist.</p>
          <a href="/" className="mt-4 inline-block text-primary hover:underline">
            Go to main site
          </a>
        </div>
      </div>
    );
  }

  // Clone children and pass orgData
  return React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { orgData, subdomain });
    }
    return child;
  });
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [superAdmin, setSuperAdmin] = useState(null);
  const [featureFlags, setFeatureFlags] = useState(null);

  const fetchFeatureFlags = async () => {
    try {
      const res = await axios.get(`${API}/organization/current`);
      setFeatureFlags(res.data?.feature_flags || {});
    } catch (e) {
      // Client users may not have org access; default to empty
      setFeatureFlags({});
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const superAdminToken = localStorage.getItem('superAdminToken');
    
    if (superAdminToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${superAdminToken}`;
      fetchSuperAdmin();
    } else if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data?.role !== 'client') {
        await fetchFeatureFlags();
      }
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const fetchSuperAdmin = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/me`);
      setSuperAdmin(response.data);
    } catch (error) {
      localStorage.removeItem('superAdminToken');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    // Clear any stale super-admin session so we don't collide tokens/state
    localStorage.removeItem('superAdminToken');
    setSuperAdmin(null);
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    if (userData?.role !== 'client') {
      fetchFeatureFlags();
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setFeatureFlags(null);
  };

  // Friendly handling for token expiry: if any authenticated request returns 401
  // and we have a stored token, clear it, surface a toast, and redirect to /login
  // (handled by the route guards once `user` becomes null).
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        // Only act on 401s for authenticated calls (skip the login/forgot/reset endpoints
        // so their own error toasts still render correctly).
        const isAuthCall = /\/api\/(auth\/(login|forgot-password|reset-password)|super-admin\/login)\b/.test(url);
        if (status === 401 && !isAuthCall) {
          const hadToken = !!localStorage.getItem('token') || !!localStorage.getItem('superAdminToken');
          if (hadToken) {
            localStorage.removeItem('token');
            localStorage.removeItem('superAdminToken');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            setSuperAdmin(null);
            setFeatureFlags(null);
            toast.info('Your session has expired. Please sign in again.');
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const superAdminLogin = (adminData) => {
    // Clear any stale consultant/client session first
    localStorage.removeItem('token');
    setUser(null);
    setFeatureFlags(null);
    const saToken = localStorage.getItem('superAdminToken');
    if (saToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${saToken}`;
    }
    setSuperAdmin(adminData);
  };

  const superAdminLogout = () => {
    localStorage.removeItem('superAdminToken');
    delete axios.defaults.headers.common['Authorization'];
    setSuperAdmin(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Client users go directly to their client dashboard
  const isClient = user?.role === 'client';
  const defaultRoute = isClient ? '/portal' : '/dashboard';

  return (
    <AuthContext.Provider value={{ user, login, logout, featureFlags, refreshFeatureFlags: fetchFeatureFlags }}>
      <OrganizationProvider>
        <BrowserRouter>
          <SubdomainHandler>
              <Suspense fallback={<RouteSplash />}>
              <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={!user ? <LandingPage /> : <Navigate to={defaultRoute} />}
              />
              <Route
                path="/signup"
                element={!user ? <Signup /> : <Navigate to={defaultRoute} />}
              />
              <Route
                path="/login"
                element={!user ? <Login /> : <Navigate to={defaultRoute} />}
              />
              <Route
                path="/forgot-password"
                element={!user ? <ForgotPassword /> : <Navigate to={defaultRoute} />}
              />
              <Route
                path="/reset-password"
                element={!user ? <ResetPassword /> : <Navigate to={defaultRoute} />}
              />
              
              {/* Organization-specific login (path-based routing) */}
              <Route
                path="/org/:orgSlug"
                element={!user ? <OrgLogin /> : <Navigate to={defaultRoute} />}
              />
              <Route
                path="/org/:orgSlug/login"
                element={!user ? <OrgLogin /> : <Navigate to={defaultRoute} />}
              />
              
              {/* OAuth Callback */}
              <Route path="/integrations/callback" element={<OAuthCallback />} />
              
              {/* Accept Invitation (public) */}
              <Route path="/invite/:token" element={<AcceptInvite />} />
              
              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={user && !isClient ? <Dashboard /> : <Navigate to={isClient ? defaultRoute : "/login"} />}
              />
              <Route
                path="/users"
                element={user && !isClient ? <FeatureGuard feature="team"><UserManagement /></FeatureGuard> : <Navigate to={isClient ? defaultRoute : "/login"} />}
              />
              <Route
                path="/onboard"
                element={user && !isClient ? <OnboardingWizard /> : <Navigate to={isClient ? defaultRoute : "/login"} />}
              />
              <Route
                path="/notifications"
                element={user ? <Notifications /> : <Navigate to="/login" />}
              />
              <Route
                path="/client/:id"
                element={user ? <ClientDashboard /> : <Navigate to="/login" />}
              />
              <Route
                path="/account"
                element={user ? <AccountSettings /> : <Navigate to="/login" />}
              />
              <Route
                path="/payments"
                element={user?.role === 'consultant' ? <FeatureGuard feature="payments"><Payments /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/communications"
                element={user?.role === 'consultant' ? <FeatureGuard feature="communications"><Communications /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/scheduled-reports"
                element={user?.role === 'consultant' ? <FeatureGuard feature="scheduled_reports"><ScheduledReports /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/analytics"
                element={user?.role === 'consultant' ? <FeatureGuard feature="analytics"><Analytics /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/logs"
                element={user?.role === 'consultant' ? <FeatureGuard feature="logs"><Logs /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/crm"
                element={user?.role === 'consultant' ? <CRM /> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/branding"
                element={user?.role === 'consultant' ? <FeatureGuard feature="branding"><Branding /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/clients"
                element={user?.role === 'consultant' ? <FeatureGuard feature="clients"><Clients /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/performance"
                element={user?.role === 'consultant' ? <FeatureGuard feature="performance"><Performance /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/tasks"
                element={user?.role === 'consultant' ? <FeatureGuard feature="tasks"><Tasks /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/firm-performance"
                element={user?.role === 'consultant' ? <FeatureGuard feature="firm_performance"><FirmPerformance /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/inbox"
                element={user && !isClient ? <ConsultantInbox /> : <Navigate to={isClient ? '/portal' : '/login'} />}
              />
              <Route
                path="/inbox/:clientId"
                element={user && !isClient ? <ConsultantInbox /> : <Navigate to={isClient ? '/portal' : '/login'} />}
              />
              <Route
                path="/playbooks"
                element={user && !isClient ? <ConsultantPlaybooks /> : <Navigate to={isClient ? '/portal/playbooks' : '/login'} />}
              />
              <Route
                path="/my-performance"
                element={user ? <ClientPerformance /> : <Navigate to="/login" />}
              />
              <Route
                path="/portal"
                element={isClient ? <ClientPortal /> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/portal/:section"
                element={isClient ? <ClientPortal /> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/ai-settings"
                element={user?.role === 'consultant' ? <FeatureGuard feature="ai_settings"><AISettings /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/proposals"
                element={user?.role === 'consultant' ? <FeatureGuard feature="proposals"><Proposals /></FeatureGuard> : <Navigate to="/dashboard" />}
              />
              
              {/* Super Admin Routes */}
              <Route
                path="/super-admin/login"
                element={!superAdmin ? <SuperAdminLogin onLogin={superAdminLogin} /> : <Navigate to="/super-admin" />}
              />
              <Route
                path="/super-admin"
                element={superAdmin ? <SuperAdminDashboard admin={superAdmin} onLogout={superAdminLogout} /> : <Navigate to="/super-admin/login" />}
              />
              </Routes>
            </Suspense>
            <WelcomeTour />
          </SubdomainHandler>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <SplashScreen />
      </OrganizationProvider>
    </AuthContext.Provider>
  );
}

export default App;
